import React, { useEffect, useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import { getNotes, createNote } from '@/api/tapResources'
import { TapNote, TapStage } from '@/types/index'

// Feature #160 — Notes tab. Immutable, stage-tagged, author+timestamp.
interface NotesTabProps {
  applicationId: string
  currentStage: TapStage
}

const stageLabels: Record<string, string> = {
  Review: 'Review',
  PhoneScreen: 'Phone Screen',
  Interview: 'Interview',
  Assessment: 'Assessment',
  ReferenceCheck: 'Reference Check',
  Offer: 'Offer',
  // Feature #199: Decision renamed to Outcome.
  Outcome: 'Outcome',
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const NotesTab: React.FC<NotesTabProps> = ({ applicationId, currentStage }) => {
  const [notes, setNotes] = useState<TapNote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getNotes(applicationId)
      // Backend returns chronological; render newest first for feed-style display.
      setNotes([...result].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ))
    } catch {
      setError('Unable to load notes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  const handleAdd = async () => {
    if (!body.trim()) return
    setSaving(true)
    try {
      await createNote(applicationId, body.trim(), currentStage)
      setBody('')
      await load()
    } catch {
      alert('Failed to add note')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Notes</h3>

      {/* Add note form */}
      <div style={cardStyle}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder={`Add a note… (will be tagged with current stage: ${stageLabels[currentStage] || currentStage})`}
          style={{ ...inputStyle, resize: 'vertical', marginBottom: '10px' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
            Notes are immutable. Once added they cannot be edited or deleted.
          </div>
          <button onClick={handleAdd} disabled={saving || !body.trim()} style={{
            ...primaryBtn,
            opacity: saving || !body.trim() ? 0.6 : 1,
            cursor: saving || !body.trim() ? 'not-allowed' : 'pointer',
          }}>
            {saving ? 'Posting…' : 'Add note'}
          </button>
        </div>
      </div>

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading notes…</div>}
      {error && <div style={{ color: 'var(--color-magenta)' }}>{error}</div>}

      {!loading && notes.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>No notes yet.</div>
        </div>
      )}

      {notes.map((n) => (
        <div key={n.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Bug #164: prefer the resolved display name; fall back to the UUID if the lookup came up empty (legacy notes). */}
              <strong style={{ fontSize: '13px' }}>{n.authorDisplayName || n.authorUserId || 'Unknown'}</strong>
              <span style={{
                padding: '2px 8px',
                backgroundColor: 'var(--color-light-grey)',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 500,
                color: 'var(--color-dark-grey)',
              }}>
                {stageLabels[n.stageWhenAdded] || n.stageWhenAdded}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
              {formatDateTime(n.createdAt)}
            </div>
          </div>
          <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{n.body}</div>
        </div>
      ))}
    </div>
  )
}

export default NotesTab
